import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Operator UI preferences that persist across reloads (per browser). Kept out of
// showStore so they survive show switches and don't churn with rundown state.
export const useSettingsStore = create(
  persist(
    (set) => ({
      // Follow the on-air cue: auto-scroll to it when it changes. On by default
      // (the long-standing behaviour); turn off to edit without the view jumping
      // back to live when someone else takes a cue.
      followOnAir: true,
      toggleFollowOnAir: () => set((s) => ({ followOnAir: !s.followOnAir })),

      // Presentation style for the Producer view. Orthogonal to theme (4 valid
      // combinations). 'standard' = the classic dense table; 'modern' = the
      // timeline spine. Same data, actions and hotkeys either way.
      uiStyle: 'standard',
      setUiStyle: (uiStyle) => set({ uiStyle }),

      // How a cue's TYPE colour is accented on standard rows:
      //  'outline' = a coloured box around the label (the original treatment)
      //  'edge'    = a coloured left bar + light tint on the row, matching the
      //              live/next format. A/B trial toggle.
      typeAccent: 'outline',
      setTypeAccent: (typeAccent) => set({ typeAccent }),

      // Relative-time display (IN shows "in 02:38" instead of a wall clock).
      // null = follow the style default (ON in modern, OFF in standard); an
      // explicit true/false overrides. Read via effectiveRelativeTimes() so the
      // "follow style" default stays a single source of truth.
      relativeTimes: null,
      setRelativeTimes: (relativeTimes) => set({ relativeTimes }),

      // Edit lock: while on, the rundown page takes no clicks, drags or keys
      // except TAKE / SKIP / BACK (see hooks/useEditLock.js). Persisted so a
      // mid-show reload comes back locked rather than silently editable.
      editLock: false,
      toggleEditLock: () => set((s) => ({ editLock: !s.editLock })),
    }),
    { name: 'mc-ui-settings' }
  )
);

// Effective relative-time flag: honour an explicit user choice, else fall back
// to the style default (modern → on, standard → off).
export function effectiveRelativeTimes(relativeTimes, uiStyle) {
  return relativeTimes ?? (uiStyle === 'modern');
}
